import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Banner,
  Button,
  InlineError,
  Layout,
  LegacyCard,
  Select,
  TextField,
} from "@shopify/polaris";
import CriticalBanner from "../../../components/banner/criticalBanner";
import SuccessBanner from "../../../components/banner/SuccessBanner";
import Transcript from "../../../components/transcript";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getFullfillmentCheckerData,
  resetFullfillmentChecker,
} from "../../../redux/store/fullfillment/Action";
import {
  fetchByNameApi,
  resetFetchByName,
} from "../../../redux/troubleshooting/fetch-by-name/Action";
import FullfillmentOrderNameTable from "../table/FullfillmentOrderNameTable";

const emptyValidation = { ordername: "", orderId: "" };

const FullfilmentChecker = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const routeShopId = String(id ?? "");
  const dispatch = useDispatch();
  const checkerSubmission = useRef(null);
  const searchSubmission = useRef(null);
  const autoRunKey = useRef(null);

  const [orderId, setOrderId] = useState("");
  const [orderName, setOrderName] = useState("");
  const [process, setProcess] = useState("id");
  const [validation, setValidation] = useState(emptyValidation);
  const [localCheckerShopId, setLocalCheckerShopId] = useState(null);
  const [localSearchShopId, setLocalSearchShopId] = useState(null);

  const fullfillmentData = useSelector(
    (store) => store.requiredFullfillmentReducer
  );
  const orderNameList = useSelector(
    (store) => store.requireFetchByNameReducer
  );

  const checkerBelongsToRoute =
    fullfillmentData?.shopId === routeShopId;
  const searchBelongsToRoute = orderNameList?.shopId === routeShopId;
  const checkerBusy =
    (checkerBelongsToRoute && fullfillmentData.isRequest) ||
    localCheckerShopId === routeShopId;
  const searchBusy =
    (searchBelongsToRoute && orderNameList.isRequest) ||
    localSearchShopId === routeShopId;
  const isBusy = checkerBusy || searchBusy;

  const checkerResult =
    checkerBelongsToRoute && fullfillmentData.isSuccess
      ? fullfillmentData.fullfillment
      : null;
  const checkerError =
    checkerBelongsToRoute && fullfillmentData.isError
      ? fullfillmentData.error
      : null;
  const searchError =
    searchBelongsToRoute && orderNameList.isError
      ? orderNameList.error
      : null;
  const orderList = useMemo(
    () =>
      searchBelongsToRoute &&
      orderNameList.isSuccess &&
      Array.isArray(orderNameList.data)
        ? orderNameList.data
        : [],
    [
      orderNameList.data,
      orderNameList.isSuccess,
      searchBelongsToRoute,
    ]
  );

  useEffect(() => {
    checkerSubmission.current = null;
    searchSubmission.current = null;
    autoRunKey.current = null;
    setLocalCheckerShopId(null);
    setLocalSearchShopId(null);
    setOrderId("");
    setOrderName("");
    setProcess("id");
    setValidation(emptyValidation);
    dispatch(resetFullfillmentChecker());
    dispatch(resetFetchByName());
  }, [dispatch, routeShopId]);

  useEffect(() => {
    const checkerAuthError =
      checkerBelongsToRoute &&
      (fullfillmentData?.fullfillment?.message === "Actual error message" ||
        fullfillmentData?.error === "Actual error message");
    const searchAuthError =
      searchBelongsToRoute &&
      (orderNameList?.data?.message === "Actual error message" ||
        orderNameList?.error === "Actual error message");
    if (checkerAuthError || searchAuthError) navigate("/logout");
  }, [
    checkerBelongsToRoute,
    fullfillmentData.error,
    fullfillmentData.fullfillment,
    navigate,
    orderNameList.data,
    orderNameList.error,
    searchBelongsToRoute,
  ]);

  const runChecker = useCallback(
    async (selectedOrderId, selectedOrderName = null) => {
      const targetShopId = routeShopId;
      if (
        !targetShopId ||
        checkerSubmission.current ||
        searchSubmission.current ||
        searchBusy ||
        checkerBusy
      ) {
        return;
      }

      const submission = { shopId: targetShopId };
      checkerSubmission.current = submission;
      setLocalCheckerShopId(targetShopId);
      setValidation(emptyValidation);

      try {
        await dispatch(
          getFullfillmentCheckerData({
            orderId: selectedOrderId,
            id: targetShopId,
            orderName: selectedOrderName,
          })
        );
      } finally {
        if (checkerSubmission.current === submission) {
          checkerSubmission.current = null;
          setLocalCheckerShopId(null);
        }
      }
    }, [checkerBusy, dispatch, routeShopId, searchBusy]
  );

  useEffect(() => {
    if (
      !searchBelongsToRoute ||
      !orderNameList.isSuccess ||
      orderList.length !== 1 ||
      !orderNameList.requestId ||
      searchBusy ||
      checkerBusy
    ) {
      return;
    }

    const onlyOrderId = orderList[0]?.orderId;
    if (onlyOrderId === null || onlyOrderId === undefined) return;
    const key = `${routeShopId}:${orderNameList.requestId}:${onlyOrderId}`;
    if (autoRunKey.current === key) return;
    autoRunKey.current = key;
    runChecker(onlyOrderId);
  }, [
    orderList,
    orderNameList.isSuccess,
    orderNameList.requestId,
    routeShopId,
    runChecker,
    searchBelongsToRoute,
    searchBusy,
    checkerBusy,
  ]);

  const handleProcessChange = useCallback(
    (newValue) => {
      if (newValue === process || isBusy) return;
      setProcess(newValue);
      setValidation(emptyValidation);
      autoRunKey.current = null;
      dispatch(resetFullfillmentChecker());
      dispatch(resetFetchByName());
    }, [dispatch, isBusy, process]
  );

  const handleSubmit = useCallback(async () => {
    const targetShopId = routeShopId;
    if (
      !targetShopId ||
      isBusy ||
      checkerSubmission.current ||
      searchSubmission.current
    ) {
      return;
    }

    if (process === "name") {
      const targetOrderName = orderName.trim();
      if (!targetOrderName) {
        setValidation({
          ordername: "Please enter an order name",
          orderId: "",
        });
        return;
      }

      const submission = { shopId: targetShopId };
      searchSubmission.current = submission;
      autoRunKey.current = null;
      setLocalSearchShopId(targetShopId);
      setValidation(emptyValidation);
      dispatch(resetFullfillmentChecker());

      try {
        await dispatch(
          fetchByNameApi({
            orderName: targetOrderName,
            storeId: targetShopId,
          })
        );
      } finally {
        if (searchSubmission.current === submission) {
          searchSubmission.current = null;
          setLocalSearchShopId(null);
        }
      }
      return;
    }

    const targetOrderId = orderId.trim();
    if (!targetOrderId) {
      setValidation({
        ordername: "",
        orderId: "Please enter an order ID",
      });
      return;
    }

    dispatch(resetFetchByName());
    await runChecker(targetOrderId);
  }, [dispatch, isBusy, orderId, orderName, process, routeShopId, runChecker]);

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    }
  };

  const options = [
    { label: "Order Name", value: "name" },
    { label: "Order ID", value: "id" },
  ];
  const hasSearchResult =
    searchBelongsToRoute && orderNameList.isSuccess;
  const resultMessage = checkerResult?.message || checkerError || "";
  const resultSucceeded = checkerResult?.success === true;
  const hasCheckerResult = Boolean(checkerResult || checkerError);
  const transcript = checkerResult?.responseData;

  return (
    <Layout.Section>
      <div className="wrapper">
        <LegacyCard sectioned>
          <div style={{ padding: "15px" }}>
            <LegacyCard sectioned>
              <div
                className="d-flex flex_wrap"
                style={{ alignItems: "end", gap: "35px" }}
              >
                <Select
                  label="Find order by"
                  options={options}
                  onChange={handleProcessChange}
                  value={process}
                  disabled={isBusy}
                />
                {process === "id" ? (
                  <div onKeyDown={handleKeyPress}>
                    <TextField
                      label="Store order ID"
                      value={orderId}
                      onChange={setOrderId}
                      autoComplete="off"
                      placeholder="Please enter the store order ID"
                      disabled={isBusy}
                    />
                    <div style={{ position: "absolute" }}>
                      <InlineError
                        message={validation.orderId}
                        fieldID="fulfillment-order-id"
                      />
                    </div>
                  </div>
                ) : (
                  <div onKeyDown={handleKeyPress}>
                    <TextField
                      label="Order name"
                      value={orderName}
                      onChange={setOrderName}
                      autoComplete="off"
                      placeholder="Please enter the order name"
                      disabled={isBusy}
                    />
                    <div style={{ position: "absolute" }}>
                      <InlineError
                        message={validation.ordername}
                        fieldID="fulfillment-order-name"
                      />
                    </div>
                  </div>
                )}
                <Button
                  primary
                  loading={isBusy}
                  disabled={isBusy}
                  onClick={handleSubmit}
                >
                  Submit
                </Button>
              </div>

              {searchError ? (
                <div style={{ marginTop: "20px" }}>
                  <Banner title="Order lookup failed" status="critical">
                    <p>{searchError}</p>
                  </Banner>
                </div>
              ) : orderList.length > 1 && process === "name" ? (
                <FullfillmentOrderNameTable
                  handleMultipleOrderNameList={runChecker}
                  list={orderList}
                  loading={searchBusy}
                  disabled={isBusy}
                />
              ) : hasSearchResult && orderList.length === 0 && process === "name" ? (
                <div style={{ marginTop: "20px" }}>
                  <Banner title="No orders found" status="warning" />
                </div>
              ) : null}

              {hasCheckerResult && resultSucceeded ? (
                <SuccessBanner message={resultMessage} runTime={1} />
              ) : hasCheckerResult ? (
                <CriticalBanner message={resultMessage} runTime={1} />
              ) : null}
              {checkerResult && transcript !== null && transcript !== undefined ? (
                <Transcript
                  displayticket={checkerResult.createTicket}
                  transcript={transcript}
                />
              ) : null}
            </LegacyCard>
          </div>
        </LegacyCard>
      </div>
    </Layout.Section>
  );
};

export default FullfilmentChecker;
